import {Decimal} from "decimal.js";
import {produce} from "immer";

import * as constants from "@client/constants";

import {server} from "@reactivated";

export type EntryLike =
    | server.journal.nutrition.InlineEntry.output
    | server.journal.nutrition.ExpandedEntry.output;

export type InlineEntryOnlyProperties = Omit<
    server.journal.nutrition.ExpandedEntry.output,
    keyof server.journal.nutrition.InlineEntry.output
>;

export const getMetrics = ({useKilojoules}: {useKilojoules: boolean}) => {
    const converted = constants.CORE_METRICS.map((metric) => {
        if (metric[0] === "calories" && useKilojoules === true) {
            return [metric[0], metric[1], metric[2], "kj", "Kilojoules"] as const;
        }
        return metric;
    });

    return {
        CORE_METRICS: converted,
        OPTIONAL_METRICS: constants.OPTIONAL_METRICS,
        ALL_METRICS: [...converted, ...constants.OPTIONAL_METRICS],
    };
};

export interface Calculations {
    calories: Decimal;
    protein: Decimal;
    fat: Decimal;
    carbs: Decimal;
    net_carbs: Decimal;

    fiber?: Decimal | null;
    sodium?: Decimal | null;
    saturated_fat?: Decimal | null;
    monounsaturated_fat?: Decimal | null;
    polyunsaturated_fat?: Decimal | null;
    trans_fat?: Decimal | null;
    cholesterol?: Decimal | null;
    sugars?: Decimal | null;
    added_sugars?: Decimal | null;
    potassium?: Decimal | null;
    magnesium?: Decimal | null;

    vitamin_a_dv?: Decimal | null;
    vitamin_c_dv?: Decimal | null;
    calcium_dv?: Decimal | null;
    iron_dv?: Decimal | null;
}

export function updateEntryToMatchIngredients<T extends EntryLike>(
    entry: T,
    calculations: Calculations,
) {
    return produce(entry, (draftState) => {
        for (const [metric, metricPg] of getMetrics({useKilojoules: false})
            .ALL_METRICS) {
            draftState[metricPg] = calculations[metric]?.toString() ?? null;
        }
    });
}

export const calculateEntryMetrics = ({
    entry,
    quantity,
    unit,
}: {
    entry:
        | server.journal.nutrition.Entry.output
        | server.journal.nutrition.Recipe.output
        | server.journal.nutrition.InlineEntry.output;
    quantity: DecimalValue | null;
    unit: string;
}) => {
    const calculations: Calculations = {
        calories: new Decimal(0),
        carbs: new Decimal(0),
        fat: new Decimal(0),
        net_carbs: new Decimal(0),
        protein: new Decimal(0),
    };

    if (quantity == null) {
        return calculations;
    }

    for (const [metric, metricPg] of getMetrics({useKilojoules: false}).ALL_METRICS) {
        const divisor = entry.units.find((u) => u.unit == unit)!.multiplier;
        const value = entry[metricPg];

        if (value != null) {
            calculations[metric] = new Decimal(value)
                .dividedBy(divisor)
                .times(quantity);
        }
    }

    return calculations;
};

export const calculateMealMetrics = (
    meal:
        | server.journal.nutrition.Meal.output
        | {
              entries: Array<
                  | server.journal.nutrition.Entry.output
                  | {
                        entry:
                            | server.journal.nutrition.Entry.output
                            | server.journal.nutrition.InlineEntry.output;
                        quantity: DecimalValue | null;
                        unit: string;
                    }
              >;
          },
) => {
    const calculations: Calculations = {
        calories: new Decimal(0),
        carbs: new Decimal(0),
        fat: new Decimal(0),
        net_carbs: new Decimal(0),
        protein: new Decimal(0),
    };

    for (const entry of meal.entries) {
        const entryCalculations = calculateEntryMetrics(
            "usda_food_id" in entry
                ? {
                      entry,
                      quantity: entry.quantity,
                      unit: entry.unit,
                  }
                : {
                      entry: entry.entry,
                      quantity: entry.quantity,
                      unit: entry.unit,
                  },
        );

        for (const [metric] of getMetrics({useKilojoules: false}).ALL_METRICS) {
            const entryValue = entryCalculations[metric];

            if (entryValue != null) {
                const currentValue = calculations[metric] ?? new Decimal(0);
                calculations[metric] = currentValue.plus(entryValue);
            }
        }
    }

    return calculations;
};

export type SummaryCalculations = {
    protein: {
        total: string | null;
        percentage: number;
    };
    fat: {
        total: string | null;
        percentage: number;
    };
    carbs: {
        total: string | null;
        percentage: number;
    };
};

export type RoundedCalculations = {[P in keyof Calculations]?: string};

export const getRoundedCalculations = (
    calculations: Calculations | Partial<Calculations>,
    useKilojoules: boolean,
    divideByServings?: DecimalValue,
): RoundedCalculations & {summary: SummaryCalculations} => {
    const rounded = {} as RoundedCalculations;
    function getKeys<T extends typeof calculations>(object: T) {
        return Object.keys(object) as (keyof T)[];
    }

    for (const key of getKeys(calculations)) {
        let value = calculations[key];

        if (value != null && useKilojoules && key === "calories") {
            value = value.dividedBy(constants.KILOJOULES_MULTIPLIER);
        }

        if (value != null) {
            rounded[key] = value
                .dividedBy(divideByServings ?? 1)
                .round()
                .toString();
        }
    }

    const protein = calculations.protein?.toNumber() ?? 0;
    const fat = calculations.fat?.toNumber() ?? 0;
    const carbs = calculations.carbs?.toNumber() ?? 0;

    const caloriesFromProteinFatAndCarbs = protein * 4 + fat * 9 + carbs * 4;

    const summary = {
        protein: {
            total: rounded.protein ?? "0",
            percentage: Math.round(
                ((protein * 4) / caloriesFromProteinFatAndCarbs) * 100,
            ),
        },
        fat: {
            total: rounded.fat ?? "0",
            percentage: Math.round(((fat * 9) / caloriesFromProteinFatAndCarbs) * 100),
        },
        carbs: {
            total: rounded.carbs ?? "0",
            percentage: Math.round(
                ((carbs * 4) / caloriesFromProteinFatAndCarbs) * 100,
            ),
        },
    };

    return {...rounded, summary};
};

export const calculateRoundedDayMetrics = (
    day: server.journal.nutrition.Day.output,
    target: server.journal.nutrition.Target.output | null,
    useKilojoules: boolean,
) => {
    const calculations: Calculations = {
        calories: new Decimal(0),
        carbs: new Decimal(0),
        fat: new Decimal(0),
        net_carbs: new Decimal(0),
        protein: new Decimal(0),

        sodium: new Decimal(0),
    };

    /* This is done in the getDay selector, so this is not needed.
       for (let metric of METRICS) {
    // TODO: load from default, or load from actual day.
    const rawValue = day[`${metric}_target`] || (day.target != null ? day.target[`${metric}_target`] : null);
    console.log(rawValue);
    calculations[`${metric}_target`] = rawValue ? new Decimal(rawValue) : null;
    }
     */

    for (const meal of day.meals) {
        const mealCalculations = calculateMealMetrics(meal);

        for (const [metric] of getMetrics({useKilojoules}).ALL_METRICS) {
            const mealValue = mealCalculations[metric];

            if (mealValue != null) {
                const currentValue = calculations[metric] ?? new Decimal(0);
                calculations[metric] = currentValue.plus(mealValue);
            }
        }
    }

    const aggregates = {
        total: {...calculations},
        remaining: {} as Partial<Calculations>,
        target: {} as Partial<Calculations>,
        percentages: {} as Partial<Calculations>,
    };

    for (const [metric, , metricTarget] of getMetrics({useKilojoules}).ALL_METRICS) {
        const dayTargetValue = target?.[metricTarget];

        if (dayTargetValue != null) {
            const target = new Decimal(dayTargetValue);
            aggregates.target[metric] = target;

            const dayTotalValue = calculations[metric];

            if (dayTotalValue != null) {
                aggregates.remaining[metric] = target.minus(dayTotalValue);
                aggregates.percentages[metric] = Decimal.min(
                    dayTotalValue.dividedBy(target).times(100),
                    new Decimal(100),
                );
            }
        }
    }

    return {
        total: getRoundedCalculations(aggregates.total, useKilojoules),
        remaining: getRoundedCalculations(aggregates.remaining, useKilojoules),
        target: getRoundedCalculations(aggregates.target, useKilojoules),
        percentages: getRoundedCalculations(aggregates.percentages, useKilojoules),
    };
};

export const getRoundedQuantity = (quantity: DecimalValue | null) => {
    return new Decimal(quantity ?? 0).toDecimalPlaces(2).toString();
};
